/**
 * Permission helpers for the admin frontend.
 * Mirrors the backend's admin:* wildcard semantics.
 */

import type { AdminBootstrap } from "@/features/auth/services/admin-auth.service";

const WILDCARD = "admin:*";

export function hasPermission(
	bootstrap: AdminBootstrap | null,
	required: string,
): boolean {
	const perms = bootstrap?.authorization.permissions;
	if (!perms || perms.length === 0) return false;
	return perms.includes(WILDCARD) || perms.includes(required);
}

/**
 * True if the operator can see the given admin section. Used for nav gating.
 */
/**
 * The admin sections the shell can gate.
 *
 * EXPORTED and consumed by `admin-shell.tsx` as its `NavSection`. The two were
 * written out separately and drifted: this list carried `logs` and `roles`
 * while the shell's did not, so both gates were dead code and neither screen
 * was reachable. One list means a section cannot be gated without the shell
 * knowing it exists.
 */
export type AdminNavSection =
	| "approvals"
	| "payments"
	| "users"
	| "catalog"
	| "gst"
	| "boqTemplates"
	| "logs"
	| "roles"
	| "vendors";

export function canSeeNav(
	bootstrap: AdminBootstrap | null,
	section: AdminNavSection,
): boolean {
	switch (section) {
		case "approvals":
			return hasPermission(bootstrap, "admin:approvals:read");
		case "payments":
			return hasPermission(bootstrap, "admin:payments:read");
		case "users":
			return hasPermission(bootstrap, "admin:users:read");
		case "catalog":
			return hasPermission(bootstrap, "admin:catalog:read");
		// GST rates (#514): catalogue readers, and finance_ops through the write
		// permission it holds without catalogue read.
		case "gst":
			return (
				hasPermission(bootstrap, "admin:catalog:read") ||
				hasPermission(bootstrap, "admin:catalog:gst")
			);
		case "boqTemplates":
			return hasPermission(bootstrap, "admin:boq-templates:read");
		case "logs":
			return hasPermission(bootstrap, "admin:logs:read");
		case "roles":
			return hasPermission(bootstrap, "admin:roles:read");
		case "vendors":
			return hasPermission(bootstrap, "admin:vendors:verify");
	}
}
